import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import usePlan from "../hooks/usePlan";
import UpgradePrompt from "../components/UpgradePrompt";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { fetchWithFallback } from "../utils/fetchWithFallback";
import API from "../api/axios";
import { saveToCache, loadFromCache } from "../utils/cache";
import { useTheme } from "../context/ThemeContext";
import toast from "react-hot-toast"

export default function Reports() {
  const { theme } = useTheme();
  const [summary, setSummary] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [dailyRevenue, setDailyRevenue] = useState([]);
  const [stockValue, setStockValue] = useState({ cost: 0, selling: 0, items: 0 });
  const [loading, setLoading] = useState(true);
  const { isExpired } = usePlan();
  const [expenseSummary, setExpenseSummary] = useState(null);

  // Shared theme styles
  const card = {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: "14px",
    padding: "28px",
    marginBottom: "32px",
    boxShadow: theme.cardShadow,
  };
  const kpiCard = {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: "12px",
    padding: "16px",
    boxShadow: theme.cardShadow,
  };

  const fetchReports = async () => {
    setLoading(true);
    const [s, t, d, exp] = await Promise.all([
      fetchWithFallback("/reports/summary", "ss_cache_reports_summary"),
      fetchWithFallback("/reports/top-products", "ss_cache_reports_top"),
      fetchWithFallback("/reports/daily-revenue", "ss_cache_reports_daily"),
      fetchWithFallback("/expenses/summary", "ss_cache_expenses_summary"),
    ]);
    if (s.data) setSummary(s.data);
    if (t.data) setTopProducts(t.data);
    if (d.data) setDailyRevenue(d.data);
    if (exp.data) setExpenseSummary(exp.data);

    if (navigator.onLine) {
      try {
        const invRes = await API.get("/reports/summary");
        const inv = invRes.data?.inventory;
        if (inv) {
          const val = { items: inv.total_items ?? 0, cost: inv.total_cost_value ?? 0, selling: inv.total_selling_value ?? 0 };
          setStockValue(val);
          saveToCache("ss_cache_inventory", val);
        }
      } catch {
        const cached = loadFromCache("ss_cache_inventory");
        if (cached) setStockValue(cached);
      }
    } else {
      const cached = loadFromCache("ss_cache_inventory");
      if (cached) setStockValue(cached);
    }
    setLoading(false);
  };

const handleWeeklyExport = async () => {
  try {
    const response = await API.get("/reports/export/weekly", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = "martsense-weekly-report.csv";
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Weekly report downloaded");
  } catch (err) {
    toast.error(err.response?.data?.detail || "Export failed");
  }
};

const handleMonthlyExport = async () => {
  try {
    const response = await API.get("/reports/export/monthly", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = "martsense-monthly-report.csv";
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Monthly report downloaded");
  } catch (err) {
    toast.error(err.response?.data?.detail || "Export failed");
  }
};

  useEffect(() => { fetchReports(); }, []);

  const sectionLabel = (text, marginTop = "24px") => (
    <p style={{
      fontSize: "12px", fontWeight: "700", color: theme.textSecondary,
      textTransform: "uppercase", letterSpacing: "1px",
      marginBottom: "12px", marginTop,
    }}>{text}</p>
  );

  if (isExpired) {
    return (
      <Layout>
        <h1 style={{ fontSize: "28px", fontWeight: "700", color: theme.textPrimary, marginBottom: "4px" }}>Reports</h1>
        <p style={{ fontSize: "14px", color: theme.textSecondary, marginBottom: "32px" }}>Business performance overview</p>
        <UpgradePrompt feature="Reports & Analytics" message="Subscribe to access full reports, revenue trends and top product analytics." />
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
        <div>
          <h1 style={{ fontSize: "28px", fontWeight: "700", color: theme.textPrimary, letterSpacing: "-0.5px" }}>Reports</h1>
          <p style={{ fontSize: "14px", color: theme.textSecondary, marginTop: "4px" }}>Business performance overview</p>
        </div>
      </div>

      {/* Period Export */}
<div style={{ ...card, marginBottom: "32px" }}>
  <h2 style={{ fontSize: "16px", fontWeight: "600", color: theme.textPrimary, marginBottom: "8px" }}>
    Download Period Reports
  </h2>
  <p style={{ fontSize: "13px", color: theme.textSecondary, marginBottom: "20px", lineHeight: "1.6" }}>
    Export a detailed spreadsheet of all completed sales including product, quantity, price, cost and profit for the selected period.
  </p>
  <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
    <button onClick={handleWeeklyExport} style={{
      background: "none", border: "1px solid #2ec4b6",
      borderRadius: "10px", padding: "12px 24px",
      color: "#2ec4b6", fontSize: "14px",
      fontWeight: "600", cursor: "pointer",
    }}>
      ⬇ This Week's Report
    </button>
    <button onClick={handleMonthlyExport} style={{
      background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
      border: "none", borderRadius: "10px", padding: "12px 24px",
      color: "#0a0f0f", fontSize: "14px",
      fontWeight: "700", cursor: "pointer",
    }}>
      ⬇ This Month's Report
    </button>
  </div>
</div>

      {loading ? (
        <p style={{ color: theme.textSecondary }}>Loading...</p>
      ) : (
        <>
          {/* TODAY */}
          {sectionLabel("Today", "0")}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "32px" }}>
            {[
              { label: "Sales", value: summary?.today?.total_sales ?? 0, suffix: "transactions", color: "#2ec4b6" },
              { label: "Revenue", value: `₦${(summary?.today?.total_revenue ?? 0).toLocaleString()}`, suffix: "earned today", color: "#2ec4b6" },
              { label: "Gross Profit", value: `₦${(summary?.today?.total_profit ?? 0).toLocaleString()}`, suffix: "before expenses", color: "#2ecc71" },
              {
                label: "Net Profit",
                value: `₦${((summary?.today?.total_profit ?? 0) - (expenseSummary?.today ?? 0)).toLocaleString()}`,
                suffix: "profit minus expenses",
                color: ((summary?.today?.total_profit ?? 0) - (expenseSummary?.today ?? 0)) >= 0 ? "#2ecc71" : "#e74c3c",
              },
            ].map(c => (
              <div key={c.label} style={kpiCard}>
                <p style={{ fontSize: "13px", color: theme.textSecondary, fontWeight: "500", marginBottom: "12px" }}>{c.label}</p>
                <p style={{ fontSize: "24px", fontWeight: "700", color: c.color, letterSpacing: "-1px" }}>{c.value}</p>
                <p style={{ fontSize: "12px", color: theme.textMuted, marginTop: "6px" }}>{c.suffix}</p>
              </div>
            ))}
          </div>

          {/* THIS MONTH */}
          {sectionLabel("This Month")}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "32px" }}>
            {[
              { label: "Total Sales", value: summary?.this_month?.total_sales ?? 0, suffix: "transactions", color: "#2ec4b6" },
              { label: "Revenue", value: `₦${(summary?.this_month?.total_revenue ?? 0).toLocaleString()}`, suffix: "this month", color: "#2ec4b6" },
              { label: "Gross Profit", value: `₦${(summary?.this_month?.total_profit ?? 0).toLocaleString()}`, suffix: "before expenses", color: "#2ecc71" },
              {
                label: "Net Profit",
                value: `₦${((summary?.this_month?.total_profit ?? 0) - (expenseSummary?.this_month ?? 0)).toLocaleString()}`,
                suffix: "profit minus expenses",
                color: ((summary?.this_month?.total_profit ?? 0) - (expenseSummary?.this_month ?? 0)) >= 0 ? "#2ecc71" : "#e74c3c",
              },
            ].map(c => (
              <div key={c.label} style={kpiCard}>
                <p style={{ fontSize: "13px", color: theme.textSecondary, fontWeight: "500", marginBottom: "12px" }}>{c.label}</p>
                <p style={{ fontSize: "24px", fontWeight: "700", color: c.color, letterSpacing: "-1px" }}>{c.value}</p>
                <p style={{ fontSize: "12px", color: theme.textMuted, marginTop: "6px" }}>{c.suffix}</p>
              </div>
            ))}
          </div>

          {/* EXPENSES */}
          {sectionLabel("Expenses")}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "32px" }}>
            {[
              { label: "Today's Expenses", value: `₦${(expenseSummary?.today ?? 0).toLocaleString()}`, suffix: "spent today", color: "#e74c3c" },
              { label: "Monthly Expenses", value: `₦${(expenseSummary?.this_month ?? 0).toLocaleString()}`, suffix: "spent this month", color: "#e74c3c" },
            ].map(c => (
              <div key={c.label} style={kpiCard}>
                <p style={{ fontSize: "13px", color: theme.textSecondary, fontWeight: "500", marginBottom: "12px" }}>{c.label}</p>
                <p style={{ fontSize: "24px", fontWeight: "700", color: c.color, letterSpacing: "-1px" }}>{c.value}</p>
                <p style={{ fontSize: "12px", color: theme.textMuted, marginTop: "6px" }}>{c.suffix}</p>
              </div>
            ))}
          </div>

          {/* INVENTORY VALUE */}
          <div style={card}>
            <h2 style={{ fontSize: "16px", fontWeight: "600", color: theme.textPrimary, marginBottom: "24px" }}>📦 Inventory Value</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "20px" }}>
              {[
                { label: "Total Items in Stock", value: stockValue.items.toLocaleString(), color: theme.textPrimary },
                { label: "Total Cost Value", value: `₦${stockValue.cost.toLocaleString()}`, color: "#f5a623" },
                { label: "Total Selling Value", value: `₦${stockValue.selling.toLocaleString()}`, color: "#2ec4b6" },
                { label: "Potential Profit", value: `₦${(stockValue.selling - stockValue.cost).toLocaleString()}`, color: "#2ecc71" },
              ].map(item => (
                <div key={item.label} style={{ background: theme.inputBg, borderRadius: "10px", padding: "16px" }}>
                  <p style={{ fontSize: "12px", color: theme.textSecondary, marginBottom: "8px" }}>{item.label}</p>
                  <p style={{ fontSize: "22px", fontWeight: "700", color: item.color, letterSpacing: "-0.5px" }}>{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 30-DAY REVENUE CHART */}
          <div style={card}>
            <h2 style={{ fontSize: "16px", fontWeight: "600", color: theme.textPrimary, marginBottom: "24px" }}>30-Day Revenue Trend</h2>
            {dailyRevenue.length === 0 ? (
              <p style={{ color: theme.textSecondary, textAlign: "center", padding: "40px 0" }}>No revenue data yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={dailyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.divider} />
                  <XAxis dataKey="date" tick={{ fill: theme.textSecondary, fontSize: 12 }}
                    tickFormatter={(d) => new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", timeZone: "Africa/Lagos" })} />
                  <YAxis tick={{ fill: theme.textSecondary, fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }}
                    labelStyle={{ color: theme.textPrimary }}
                    itemStyle={{ color: "#2ec4b6" }}
                    formatter={(v) => [`₦${v.toLocaleString()}`, "Revenue"]}
                  />
                  <Line type="monotone" dataKey="total_revenue" stroke="#2ec4b6" strokeWidth={2} dot={{ fill: "#2ec4b6", r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* TOP PRODUCTS */}
          <div style={card}>
            <h2 style={{ fontSize: "16px", fontWeight: "600", color: theme.textPrimary, marginBottom: "24px" }}>Top Selling Products</h2>
            {topProducts.length === 0 ? (
              <p style={{ color: theme.textSecondary, textAlign: "center", padding: "40px 0" }}>No sales data yet</p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={topProducts}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.divider} />
                    <XAxis dataKey="name" tick={{ fill: theme.textSecondary, fontSize: 12 }} />
                    <YAxis tick={{ fill: theme.textSecondary, fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }}
                      labelStyle={{ color: theme.textPrimary }}
                      itemStyle={{ color: "#2ec4b6" }}
                    />
                    <Bar dataKey="total_quantity_sold" fill="#2ec4b6" radius={[4, 4, 0, 0]} name="Units Sold" />
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", marginTop: "24px" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "480px" }}>
                    <thead>
                      <tr>
                        {["Product", "SKU", "Units Sold", "Revenue"].map((h) => (
                          <th key={h} style={{
                            textAlign: "left", padding: "12px 16px", fontSize: "12px",
                            fontWeight: "600", color: theme.textSecondary,
                            textTransform: "uppercase", letterSpacing: "0.5px",
                            borderBottom: `1px solid ${theme.divider}`,
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {topProducts.map((p) => (
                        <tr key={p.sku} style={{ borderBottom: `1px solid ${theme.divider}` }}>
                          <td style={{ padding: "12px 16px", fontSize: "14px", color: theme.textPrimary }}>{p.name}</td>
                          <td style={{ padding: "12px 16px", fontSize: "14px", color: theme.textSecondary, fontFamily: "monospace" }}>{p.sku}</td>
                          <td style={{ padding: "12px 16px", fontSize: "14px", color: "#2ec4b6", fontWeight: 600 }}>{p.total_quantity_sold}</td>
                          <td style={{ padding: "12px 16px", fontSize: "14px", color: theme.textPrimary }}>₦{Number(p.total_revenue).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </Layout>
  );
}